import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { FollowUserButton } from '@root/features/user/activity-actions/follow-user-button'
import { environmentAtom } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { Avatar } from '@root/shared/ui-kit'

import type { WalletProfile } from '../model'
import type { DelugeId } from '@root/entities/user-short'
import type { AvatarsHost } from '@root/shared/models'

/**
 * The person behind the wallet.
 *
 * An address is a stranger by default, and most of them stay that way. When
 * one belongs to somebody here, this is the difference between reading a
 * number and reading a person: their name, how many read them, and a button
 * to read them yourself.
 *
 * Absent for a stranger, and equally absent for somebody who has gone
 * incognito — the screen cannot tell those apart and has no business trying.
 */
type Props = {
  owner: WalletProfile.Owner
}

export const WalletProfileOwner: React.FC<Props> = ({ owner }) => {
  const { t } = useTranslation()
  const [environment] = useAtom(environmentAtom)

  const avatarSrc = useMemo(
    () => avatarUrl(owner.avatar, environment.assets.avatarsHost),
    [owner.avatar, environment],
  )

  return (
    <Root>
      <Avatar size={20} src={avatarSrc} alt={owner.username} />
      <Name>{owner.username}</Name>

      <Counts>
        <Count>
          <Numeric value={owner.followers} preset="currency-amount-short" />
          <CountLabel>{t('common.follower_other')}</CountLabel>
        </Count>
        <Count>
          <Numeric value={owner.following} preset="currency-amount-short" />
          <CountLabel>{t('common.following_other')}</CountLabel>
        </Count>
        <Count>
          <Numeric value={owner.posts} preset="currency-amount-short" />
          <CountLabel>{t('comment.comments')}</CountLabel>
        </Count>
      </Counts>

      <FollowUserButton delugeId={owner.delugeId as DelugeId} />
    </Root>
  )
}

const avatarUrl = (avatar: string | undefined, host: AvatarsHost) =>
  avatar ? `${host}/${avatar}` : undefined

const Root = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  padding: 6px 10px;
  border: 1px solid var(--background-3);
  border-radius: 999px;
  background: var(--background-2);
`

const Name = styled.span`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
`

const Counts = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const Count = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: baseline;

  color: var(--text-11);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`

const CountLabel = styled.span`
  color: var(--text-9);
  font-size: 10px;
`
